import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Table } from '@quintoandar-tokko/jopijs';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import parentStyles from '../dashboardTables.module.scss';
import specificStyles from './responseTime.module.scss';
import LoadingMoreSkeleton from '../components/LoadingMoreSkeleton';
import { getPerformanceResponseTimeTable } from '@/store/slices/components/dashboard/service';
import buildQuery from '../../utils/buildQuery';
import useObserver from '@/hooks/useObserver';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';
import HeaderCell from '../components/HeaderCell';
import buildTableQuery from '../../utils/buildTableQuery';
import { TABLE_SORT_FIELDS } from '../../constants';
import ResponseTimeTableRowSingle from './ResponseTimeTableRowSingle';
import ResponseTimeTableRowMulti from './ResponseTimeTableRowMulti';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function ResponseTimeTable() {
  const dispatch = useDispatch();
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { responseTime } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { ref: loadMoreRef, isInViewport } = useObserver();
  const track = useTrackPerformanceTable();

  const singleAgentHeaders = [
    {
      id: 1,
      title: Trans(translations, 'Contact'),
      field: TABLE_SORT_FIELDS.CONTACT_NAME,
    },
    {
      id: 2,
      title: Trans(translations, 'Operation'),
      field: TABLE_SORT_FIELDS.OPERATION,
    },
    {
      id: 3,
      title: Trans(translations, 'PropertyType'),
      field: TABLE_SORT_FIELDS.PROPERTY_TYPE,
    },
    {
      id: 4,
      title: Trans(translations, 'Location'),
      field: TABLE_SORT_FIELDS.LOCATION,
    },
    {
      id: 5,
      title: Trans(translations, 'ResponseTime'),
      field: TABLE_SORT_FIELDS.RESPONSE_TIME,
    },
  ];

  const multiAgentsHeaders = [
    {
      id: 1,
      title: Trans(translations, 'Agent'),
      subtitle: Trans(translations, 'Branch'),
      field: TABLE_SORT_FIELDS.AGENT_NAME,
    },
    {
      id: 2,
      title: Trans(translations, 'MedianResponseTime'),
      field: TABLE_SORT_FIELDS.MEDIAN_RESPONSE_TIME,
    },
    {
      id: 3,
      title: Trans(translations, 'AssignedContacts'),
      field: TABLE_SORT_FIELDS.ASSIGNED_CONTACTS,
    },
    {
      id: 4,
      title: Trans(translations, 'AnsweredContacts'),
      field: TABLE_SORT_FIELDS.ANSWERED_CONTACTS,
    },
    {
      id: 5,
      title: Trans(translations, 'UnattendedContactsAndReassigned'),
      field: TABLE_SORT_FIELDS.UNATTENDED_CONTACTS_AND_REASSIGNED,
    },
  ];

  useEffect(() => {
    if (!responseTime.has_next) return;
    if (!isInViewport || responseTime.loading) return;

    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(responseTime, {
      withNextPage: true,
    });

    dispatch(getPerformanceResponseTimeTable({ query, tableQuery }));
  }, [isInViewport]);

  const headers = responseTime.is_single_agent
    ? singleAgentHeaders
    : multiAgentsHeaders;

  const handleCellClick = (header) => {
    dispatch(
      setPerformanceModals({
        key: 'responseTime',
        subkey: 'sortBy',
        value: header.field,
      })
    );
    track.responseTime(TRACK_ELEMENT.COLUMN_ORDER);
  };

  return (
    <Table
      id="response-time-table"
      className={`${parentStyles.table} ${specificStyles.responseTimeTable}`}
    >
      <Table.Header className={parentStyles.tableHeader}>
        {headers.map((header) => (
          <HeaderCell
            key={header.id}
            id={header.id}
            title={header.title}
            subtitle={header.subtitle}
            field={header.field}
            sortBy={responseTime.sortBy}
            sortDirection={responseTime.sortDirection}
            onClick={() => handleCellClick(header)}
          />
        ))}
      </Table.Header>

      <Table.Rows>
        {responseTime.list.map((row) =>
          responseTime.is_single_agent ? (
            <ResponseTimeTableRowSingle key={row.id} row={row} />
          ) : (
            <ResponseTimeTableRowMulti key={row.id} row={row} />
          )
        )}

        <Box as="tr" ref={loadMoreRef} style={{ height: 1, opacity: 0 }} />

        {(responseTime.loading || responseTime.has_next) && (
          <LoadingMoreSkeleton columns={headers.length} />
        )}
      </Table.Rows>
    </Table>
  );
}
